import { useMutation, useQuery } from '@tanstack/react-query';
import firebase from 'firebase/compat/app';
import 'firebase/compat/auth';
import 'firebase/compat/database';
import { v4 as uuid } from 'uuid';

import { firebaseGetApi, firebasePostApi } from '../api/firebaseApi';
import { updateUser } from '../api/firebaseHelpers';
import { trackLogin, trackLogout, trackSignup } from '../helpers/analytics';
import { globalQueryClient, isNilOrEmpty } from '../helpers/extra';
import i18n from '../i18n';
import { SIGNUP_TYPE_KEYS, SSO_SIGNUP_PROVIDERS } from '../pages/signup/helper';
import store from '../store/store';
import { VERIFY_SSO_ACCESS_TOKEN_URL } from './apiUser';
import { LOGOUT_USER } from './types';
import { uiError, uiLoading } from './uiAction';

export const handleLoginSuccess = (method = 'email') => {
  const usid = uuid();
  localStorage.setItem('usid', usid);
  updateUser({ usid });

  // Track login success
  trackLogin(method);

  store.dispatch(uiLoading(false));
  store.dispatch(uiError({ isError: false, message: null }));
};

const handleLoginError = ({ message = '', isEmailPasswordError = false }) => {
  store.dispatch(uiLoading(false));
  store.dispatch(uiError({ isError: true, message, isEmailPasswordError }));
};

const signupEndpoints = {
  signUpUserTrial: '/user/trial/sign-up',
  signUpUserTrialWithSSO: '/user/trial/sso-sign-up',
  createUserTrial: '/user/trial/sign-up-with-email-and-password',
  createUserTrialWithSSO: '/user/trial/sign-up-with-sso',
  verifySignupToken: '/user/trial/token',
  trialFeatures: '/user/trial/features',
};

const t = (key) => i18n.t(`common:dashboardView.${key}`, key);

const linkSsoCredentials = async (credentials, provider) => {
  const verificationResult = await firebasePostApi(
    VERIFY_SSO_ACCESS_TOKEN_URL,
    {
      provider,
      access_token: credentials.accessToken,
    },
  );

  if (verificationResult?.customToken) {
    firebase
      .auth()
      .signInWithCustomToken(verificationResult.customToken)
      .then(async (result) => {
        if (result.user) {
          // Link Microsoft credentials with user's firebase account
          await firebase.auth().currentUser.linkWithCredential(credentials);
          handleLoginSuccess(provider);
        }
      })
      .catch((e) => handleLoginError({ message: e }));
  } else {
    handleLoginError({});
  }
};

const microsoftAuthProvider = new firebase.auth.OAuthProvider('microsoft.com');

microsoftAuthProvider.setCustomParameters({
  tenant: 'consumers',
  prompt: 'select_account',
});

const googleAuthProvider = new firebase.auth.GoogleAuthProvider();
googleAuthProvider.setCustomParameters({ prompt: 'select_account' });

const handleSSOSignInError = (error) => {
  if (error.code === 'auth/account-exists-with-different-credential') {
    // Link pending Microsoft credentials to to a firebase user to enable login using microsoft account
    const { credential } = error;
    linkSsoCredentials(credential, 'microsoft');
  } else if (error.code === 'auth/admin-restricted-operation') {
    handleLoginError({ message: t('authAccessDeniedMessage') });
  } else if (error?.code === 'auth/user-disabled') {
    handleLoginError({ message: t('accountDeletedErrorMessage') });
  } else {
    handleLoginError({});
  }
};

export const loginMicrosoftUser = () => () => {
  firebase
    .auth()
    .signInWithPopup(microsoftAuthProvider)
    .then((result) => {
      if (result.user) {
        handleLoginSuccess('microsoft');
      }
    })
    .catch(handleSSOSignInError);
};

export const loginGoogleUser = () => () => {
  firebase
    .auth()
    .signInWithPopup(googleAuthProvider)
    .then((result) => {
      if (result.user) {
        handleLoginSuccess('google');
      }
    })
    .catch(handleSSOSignInError);
};

export const loginUser = (account) => async (dispatch) => {
  const email = account.email.trim();
  const password = account.password.trim();
  const trialFeature = account?.trialFeature;

  try {
    dispatch(
      uiError({ isError: false, message: null, isEmailPasswordError: false }),
    );
    dispatch(uiLoading(true));

    await firebase
      .auth()
      .signInWithEmailAndPassword(email, password)
      .then(async (result) => {
        if (result.user) {
          const userIdToken = await result.user.getIdToken();

          if (!isNilOrEmpty(trialFeature)) {
            await firebasePostApi(signupEndpoints.signUpUserTrialWithSSO, {
              token: userIdToken,
              trialFeature,
            });
          }

          handleLoginSuccess('email');
        }
      });
  } catch (err) {
    let errorMessage = t('generalLoginErrorMessage');

    if (err?.code === 'auth/invalid-credential') {
      errorMessage = t('authInvalidEmailPasswordMessage');
    } else if (err?.code === 'auth/user-disabled') {
      errorMessage = t('accountDeletedErrorMessage');
    }

    handleLoginError({
      message: errorMessage,
      isEmailPasswordError: true,
    });
  }
};

export const logoutUser = () => async (dispatch) => {
  // Get user ID before logout for analytics
  const userId = store.getState()?.user?.info?.userId;

  // Track logout event
  trackLogout(userId);

  globalQueryClient.clear();
  await firebase.auth().signOut();
  if (window.tokenRenewTimer) clearTimeout(window.tokenRenewTimer);
  localStorage.removeItem('token');
  localStorage.removeItem('access_token');
  dispatch({ type: LOGOUT_USER });
  dispatch(uiLoading(false));
};

export const useSignUpUser = () => {
  const { mutate, data, isPending, error } = useMutation({
    mutationFn: (signUpInfo) =>
      firebasePostApi(signupEndpoints.signUpUserTrial, signUpInfo),
  });

  return {
    signUp: mutate,
    data,
    isLoading: isPending,
    error,
  };
};

export const useCreateTrialUser = (userInfo, signUpType) => {
  const userInfoParams = {
    userFullName: userInfo?.userFullName,
    password: userInfo?.password,
    companyName: userInfo?.companyName,
    phoneNumber: userInfo?.phoneNumber,
    trialFeature: userInfo?.trialFeature,
    token: userInfo?.token,
  };

  const signUpEndpoint =
    signUpType === SIGNUP_TYPE_KEYS.default
      ? signupEndpoints.createUserTrial
      : signupEndpoints.createUserTrialWithSSO;

  const createUserTrialCall = () =>
    firebasePostApi(signUpEndpoint, userInfoParams);

  const isEnabled = !isNilOrEmpty(userInfo);

  const { data, isLoading, error } = useQuery({
    queryKey: [
      'create-user-trial',
      userInfoParams.trialFeature,
      signUpType,
      userInfoParams.token,
    ],
    queryFn: createUserTrialCall,
    enabled: isEnabled,
  });

  return { data, isLoading, error };
};

export const signupWithSSO = async (
  selectedProvider,
  trialFeature,
  callbackFn,
) => {
  store.dispatch(uiError({ isError: false, message: null }));
  const provider =
    selectedProvider === SSO_SIGNUP_PROVIDERS.google
      ? googleAuthProvider
      : microsoftAuthProvider;

  try {
    const result = await firebase.auth().signInWithPopup(provider);

    if (result.user) {
      handleLoginSuccess(selectedProvider);

      const userIdToken = await result.user.getIdToken();

      const data = await firebasePostApi(
        signupEndpoints.signUpUserTrialWithSSO,
        {
          token: userIdToken,
          trialFeature,
        },
      );

      // Track signup success
      trackSignup(selectedProvider, trialFeature);

      callbackFn && callbackFn(data);
    }
  } catch (err) {
    handleSSOSignInError(err);
  }
};

export const useVerifySignupToken = (token) => {
  const fetchTokenValidity = () => {
    const params = { token };
    return firebaseGetApi(signupEndpoints.verifySignupToken, params);
  };

  const { error } = useQuery({
    queryKey: ['verify-token', token],
    enabled: Boolean(token?.length),
    queryFn: fetchTokenValidity,
  });
  return { error };
};

export const signupForTrialFeature = (trialFeature) =>
  firebasePostApi(signupEndpoints.trialFeatures, { trialFeature });
